import { useTaskDashboard } from "@/contexts/TaskDashboardContext";
import { cn } from "@/lib/utils";

export function TaskDayProgressHeader({ compact = false }: { compact?: boolean }) {
  const { dayProgress, weekProgress, motivationalLabel, hasUrgentAttention } = useTaskDashboard();

  const { done, total } = dayProgress;
  const barWidthPercent = total > 0 ? Math.min(100, (done / total) * 100) : 0;

  if (total === 0) return null;

  if (compact) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 min-w-0 flex-1",
          hasUrgentAttention ? "text-destructive" : "",
        )}
        title={motivationalLabel || "Missão de hoje"}
      >
        <span className="text-xs font-semibold tabular-nums shrink-0">{done}/{total}</span>
        <div className="h-1.5 flex-1 min-w-8 rounded-full bg-muted overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              barWidthPercent >= 100
                ? "bg-emerald-500"
                : hasUrgentAttention
                  ? "bg-destructive"
                  : "bg-primary",
            )}
            style={{ width: `${barWidthPercent}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2 w-full min-w-0 sm:min-w-[420px] sm:max-w-xl sm:w-auto",
        hasUrgentAttention
          ? "border-destructive/40 bg-destructive/5 dark:border-destructive/55 dark:bg-destructive/15"
          : "border-amber-500/30 bg-amber-500/5 dark:border-amber-400/50 dark:bg-amber-500/12",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
        <p className="text-sm font-semibold shrink-0 dark:text-amber-50">Missão de hoje</p>
        <span className="text-sm font-semibold tabular-nums text-primary shrink-0 dark:text-amber-300">
          {done}/{total}
        </span>
        <div className="h-1.5 flex-1 min-w-[4.5rem] basis-[4.5rem] rounded-full bg-muted overflow-hidden dark:bg-amber-950/55">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              barWidthPercent >= 100
                ? "bg-emerald-500 dark:bg-emerald-400"
                : hasUrgentAttention
                  ? "bg-destructive dark:bg-red-400"
                  : "bg-gradient-to-r from-amber-500 to-amber-400 dark:from-amber-400 dark:to-amber-300",
            )}
            style={{ width: `${barWidthPercent}%` }}
          />
        </div>
        {weekProgress.percent > 0 && (
          <span className="text-xs text-muted-foreground tabular-nums shrink-0 dark:text-amber-200/80">
            Semana: {weekProgress.percent.toFixed(0)}%
          </span>
        )}
      </div>
      {motivationalLabel && (
        <p className="text-xs text-muted-foreground truncate mt-1 dark:text-amber-200/85">
          {motivationalLabel}
        </p>
      )}
    </div>
  );
}
